import {all, call, fork, put, takeEvery, takeLatest} from 'bukku/shared/SagaService';
import actions from './actions';
import AppActions from '../App/actions';
import {deleto, get, patch, post, puto} from 'bukku/shared/ApiService';
import {errorSagaFn, showBatchImportModalError, successSagaFn} from "../helper";
import {plural, t} from "@lingui/macro";
import { store } from '../store';

const successKeys = [
    actions.CREATE_PRODUCT_SUCCESS,
    actions.CREATE_PRODUCTS_SUCCESS,
    actions.READ_PRODUCTS_SUCCESS,
    actions.READ_PRODUCT_SUCCESS,
    actions.UPDATE_PRODUCT_SUCCESS,
    actions.ARCHIVE_PRODUCT_SUCCESS,
    actions.DELETE_PRODUCT_SUCCESS,
    actions.OPEN_PRODUCT_CARD_SUCCESS,
    actions.READ_PRODUCT_CARD_PAST_TXNS_SUCCESS,
    actions.MERGE_PRODUCT_SUCCESS,
    actions.BULK_UPDATE_PRODUCTS_SUCCESS,
];
const errorKeys = [
    actions.CREATE_PRODUCT_ERROR,
    // actions.CREATE_PRODUCTS_ERROR, // Batch import error, will show modal instead
    actions.READ_PRODUCTS_ERROR,
    actions.READ_PRODUCT_ERROR,
    actions.UPDATE_PRODUCT_ERROR,
    actions.ARCHIVE_PRODUCT_ERROR,
    actions.DELETE_PRODUCT_ERROR,
    actions.OPEN_PRODUCT_CARD_ERROR,
    actions.READ_PRODUCT_CARD_PAST_TXNS_ERROR,
    actions.MERGE_PRODUCT_ERROR,
    actions.BULK_UPDATE_PRODUCTS_ERROR,
];
const getSuccessMsg = action => {
    switch (action.type) {
        case actions.CREATE_PRODUCT_SUCCESS:
            return t`The product is created.`;
        case actions.UPDATE_PRODUCT_SUCCESS:
            return t`The product is updated.`;
        case actions.DELETE_PRODUCT_SUCCESS:
            return t`The product is deleted.`;
        case actions.ARCHIVE_PRODUCT_SUCCESS: {
            const { product } = action;
            return product.is_archived ? t`The product is deactivated.` : t`The product is activated.`;
        }
        case actions.CREATE_PRODUCTS_SUCCESS: {
            const { data } = action;
            return t`${data.created_count} products created and ${data.updated_count} products updated.`;
        }
        case actions.BULK_UPDATE_PRODUCTS_SUCCESS: {
            const { data } = action;
            const { updated_count } = data;
            const successMsg = plural(updated_count, {
                one: `1 product updated.`,
                other: `# product(s) updated.`,
            });
            return successMsg;
        }
        default:
            return null;
    }
};
// Create product
export function* createProductRequest() {
    yield takeEvery(actions.CREATE_PRODUCT_REQUEST, function* ({ request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });

            context.allow_intercept = true;
            const response = yield call(post, '/products', request, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.CREATE_PRODUCT_SUCCESS,
                product: data.product,
            });
        }
        catch (error) {
            yield put({
                type: actions.CREATE_PRODUCT_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}

// Create multiple products (ie batch import)
export function* createProductsRequest() {
    yield takeEvery(actions.CREATE_PRODUCTS_REQUEST, function* ({ request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });

            context.allow_intercept = true;
            const response = yield call(post, '/products/batch', request, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.CREATE_PRODUCTS_SUCCESS,
                data,
            });
        }
        catch (error) {
            yield put({
                type: actions.CREATE_PRODUCTS_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}

export function* createProductsError() {
    yield takeEvery(actions.CREATE_PRODUCTS_ERROR, function* ({ error }) {
        const { Product } = store.getState();
        yield showBatchImportModalError(error, Product.importHeaderRowLineCount, t`products`);
    });
}

// Read products
export function* readProductsRequest() {
    yield takeEvery(actions.READ_PRODUCTS_REQUEST, function* ({ request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PULLING });
            const response = yield call(get, '/products', request, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.READ_PRODUCTS_SUCCESS,
                products: data.products,
                pagination: data.paging,
            });
        }
        catch (error) {
            yield put({
                type: actions.READ_PRODUCTS_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}

// Read product
export function* readProductRequest() {
    yield takeEvery(actions.READ_PRODUCT_REQUEST, function* ({ id }, context) {
        try {
            yield put({ type: AppActions.APP_START_PULLING });
            const response = yield call(get, `/products/${id}`, null, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.READ_PRODUCT_SUCCESS,
                product: data.product,
            });
        }
        catch (error) {
            yield put({
                type: actions.READ_PRODUCT_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}

// Update product
export function* updateProductRequest() {
    yield takeEvery(actions.UPDATE_PRODUCT_REQUEST, function* ({ id, request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });

            context.allow_intercept = true;
            const response = yield call(puto, `/products/${id}`, {...request, id}, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.UPDATE_PRODUCT_SUCCESS,
                product: data.product,
            });
        }
        catch (error) {
            yield put({
                type: actions.UPDATE_PRODUCT_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}

// Archive product
export function* archiveProductRequest() {
    yield takeEvery(actions.ARCHIVE_PRODUCT_REQUEST, function* ({ id, request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });

            context.allow_intercept = true;
            const response = yield call(patch, `/products/${id}`, request, context);
            const data = yield response.data;

            yield put({
                type: actions.ARCHIVE_PRODUCT_SUCCESS,
                product: data.product,
            });
        }
        catch (error) {
            yield put({
                type: actions.ARCHIVE_PRODUCT_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}

// Delete product
export function* deleteProductRequest() {
    yield takeEvery(actions.DELETE_PRODUCT_REQUEST, function* ({ id }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });

            context.allow_intercept = true;
            yield call(deleto, `/products/${id}`, context, context);

            yield put({
                type: actions.DELETE_PRODUCT_SUCCESS,
            });
        }
        catch (error) {
            yield put({
                type: actions.DELETE_PRODUCT_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}

// Bulk update product
export function* bulkUpdateProductsRequest() {
    yield takeEvery(actions.BULK_UPDATE_PRODUCTS_REQUEST, function* ({ request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });
            // 'strict: false' so that any products that has an error won’t abort the entire process
            const response = yield call(puto, '/products/batch', {...request, strict: false}, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.BULK_UPDATE_PRODUCTS_SUCCESS,
                data,
            });
        }
        catch (error) {
            yield put({
                type: actions.BULK_UPDATE_PRODUCTS_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}

// Read bulk product
export function* readBulkProductsRequest() {
    yield takeEvery(actions.READ_BULK_PRODUCTS_REQUEST, function* ({ request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PULLING });

            const response = yield call(get, '/products/batch', {...request, is_archived: false}, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.READ_BULK_PRODUCTS_SUCCESS,
                products: data.products,
                pagination: data.paging,
            });
        }
        catch (error) {
            yield put({
                type: actions.READ_BULK_PRODUCTS_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PULLING });
        }
    });
}

// PRODUCT CARD: Read product
export function* openProductCardRequest() {
    yield takeLatest(actions.OPEN_PRODUCT_CARD_REQUEST, function* ({ id }, context) {
        try {
            const response = yield call(get, `/products/${id}`, null, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.OPEN_PRODUCT_CARD_SUCCESS,
                product: data.product,
            });
        }
        catch (error) {
            yield put({
                type: actions.OPEN_PRODUCT_CARD_ERROR,
                error,
            });
        }
    });
}

// PRODUCT CARD: Read product's past transactions
export function* readProductCardPastTxnRequest() {
    yield takeLatest(actions.READ_PRODUCT_CARD_PAST_TXNS_REQUEST, function* ({ request }, context) {
        try {
            const response = yield call(get, '/reports/transaction_list', request, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.READ_PRODUCT_CARD_PAST_TXNS_SUCCESS,
                pastTransactions: data?.report?.transactions,
            });
        }
        catch (error) {
            yield put({
                type: actions.READ_PRODUCT_CARD_PAST_TXNS_ERROR,
                error,
            });
        }
    });
}

// Merge Products
export function* mergeProductsRequest() {
    yield takeEvery(actions.MERGE_PRODUCT_REQUEST, function* ({ request }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });

            context.allow_intercept = true;
            const response = yield call(post, '/products/merge', request, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.MERGE_PRODUCT_SUCCESS,
                request,
                data,
            });
        }
        catch (error) {
            yield put({
                type: actions.MERGE_PRODUCT_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}

// Bulk Delete products
export function* bulkDeleteProductsRequest() {
    yield takeEvery(actions.BULK_DELETE_PRODUCTS_REQUEST, function* ({product_ids}) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });
            const response = yield call(deleto, '/products/bulk_product', { product_ids });
            const data = yield response.data;

            // OK
            yield put({
                type: actions.BULK_DELETE_PRODUCTS_SUCCESS,
                data,
            });
        }
        catch (error) {
            yield put({
                type: actions.BULK_DELETE_PRODUCTS_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}

export function* bulkDeleteProductsSuccess() {
    yield takeEvery(actions.BULK_DELETE_PRODUCTS_SUCCESS, () => {});
}

export function* bulkDeleteProductsError() {
    yield takeEvery(actions.BULK_DELETE_PRODUCTS_ERROR, () => {});
}

export default function* rootSaga() {
    yield all([
        fork(successSagaFn(successKeys, getSuccessMsg)),
        fork(errorSagaFn(errorKeys)),
        fork(createProductRequest),
        fork(createProductsRequest),
        fork(createProductsError),
        fork(readProductsRequest),
        fork(readProductRequest),
        fork(updateProductRequest),
        fork(archiveProductRequest),
        fork(deleteProductRequest),
        fork(openProductCardRequest),
        fork(readProductCardPastTxnRequest),
        fork(mergeProductsRequest),
        fork(bulkUpdateProductsRequest),
        fork(readBulkProductsRequest),
        fork(bulkDeleteProductsRequest),
        fork(bulkDeleteProductsSuccess),
        fork(bulkDeleteProductsError),
    ]);
}
